import { isEmpty, isNil } from 'lodash'

import { toListItems } from '@/utils/helpers'
import { ADDED, REMOVED, computeDiff, toWords } from '@/components/diff/diff.helpers'

import { normalizeFieldKey } from './index'

// What the saved note held for each field a test run produced, keyed by the
// name the run used, so the results can show the live configuration's answer
// beside the one being tested.
//
// Matching is on the normalized name: the note was written by whichever
// configuration was live at the time, so a field renamed only in case or
// spacing still lines up, while a genuine rename correctly finds nothing to
// compare against.
//
// Both maps are null-prototype: field names are author-entered, and a field
// called `__proto__` would otherwise write through the prototype setter and
// have the comparison read Object.prototype back.
export const buildPreviousValues = (parsedSummary = {}, savedNote) => {
  if (isEmpty(savedNote)) {
    return Object.create(null)
  }

  const savedByNormalizedName = Object.create(null)
  Object.entries(savedNote).forEach(([fieldName, value]) => {
    savedByNormalizedName[normalizeFieldKey(fieldName)] = value
  })

  const previousValues = Object.create(null)
  Object.keys(parsedSummary).forEach((fieldName) => {
    const normalizedName = normalizeFieldKey(fieldName)
    // Presence, not a non-nil value: a field the note holds as null still has a
    // saved answer to read against (an unresolved taxonomy, say), and dropping
    // it would render a null-to-value change as if nothing had been saved.
    if (normalizedName in savedByNormalizedName) {
      previousValues[fieldName] = savedByNormalizedName[normalizedName]
    }
  })

  return previousValues
}

// Whether the saved note had anything for this field at all — distinct from
// what it held, which may legitimately be null.
export const hasPreviousValue = (previousValues = {}, fieldName) =>
  Object.prototype.hasOwnProperty.call(previousValues, fieldName)

// A value as one block of text, with a list written the same way whichever shape
// it arrived in. The model answers a list field as a JSON array on one run and
// as bulleted lines on the next, and neither the reader nor the comparison
// should care which: `["a","b"]` against "- a\n- b" is the same note, and
// `String()` on an array would turn it into "a,b" in the diff besides.
export const toComparableText = (value) => {
  const items = toListItems(value)
  if (isNil(items)) {
    return String(value ?? '')
  }
  return items.map((item) => `- ${item}`).join('\n')
}

// Whether a tested value actually differs from what the saved note held.
// Compared as trimmed text: a field whose only change is trailing whitespace —
// or the shape its list came back in — reads as unchanged, which is what a
// reader means by "did this move".
export const hasValueChanged = (previous, current) =>
  toComparableText(previous).trim() !== toComparableText(current).trim()

// Past these, a word diff stops being readable: with most of the words struck
// or inserted, or edits scattered through the whole answer, the reader is left
// assembling both versions out of confetti. A model re-running a prose field
// rewrites it more often than it rewords it, so this is the common case for a
// long answer, not an edge.
//
// The share alone misfires on short answers: "Delivery Appointment" to
// "Delivery" is half the words, but one struck word reads perfectly well as a
// diff. Below REWRITE_MIN_CHANGED_WORDS changed words, even an answer changed
// entirely is short enough to diff inline.
const REWRITE_CHANGED_SHARE = 0.4
const REWRITE_MIN_CHANGED_WORDS = 8
const REWRITE_CHANGED_RUNS = 12

// Whether a changed answer reads better as the new text, old one on request, than
// as a word diff. Counted on words alone — whitespace tokens are part of the
// diff so it can render the text back as written, but a changed gap between two
// changed words is not a third change.
//
// An answer that went to or from nothing is never a rewrite: the diff shows it
// as one struck or inserted passage, which reads fine, while the rewrite view
// would show a blank answer or a toggle that opens an empty box.
export const isRewrite = (previousText = '', currentText = '') => {
  if (isEmpty(previousText.trim()) || isEmpty(currentText.trim())) {
    return false
  }

  // Each region between unchanged words is one edit, sized by its longer side:
  // replacing three words with three others changes three words, not six.
  let unchangedWords = 0
  let changedWords = 0
  let edits = 0
  let removed = 0
  let added = 0
  const closeEdit = () => {
    if (removed + added > 0) {
      changedWords += Math.max(removed, added)
      edits += 1
    }
    removed = 0
    added = 0
  }

  computeDiff(toWords(previousText), toWords(currentText))
    .filter((word) => word.value.trim() !== '')
    .forEach((word) => {
      if (word.type === REMOVED) {
        removed += 1
      } else if (word.type === ADDED) {
        added += 1
      } else {
        closeEdit()
        unchangedWords += 1
      }
    })
  closeEdit()

  const mostlyChanged =
    changedWords >= REWRITE_MIN_CHANGED_WORDS &&
    changedWords / (unchangedWords + changedWords) > REWRITE_CHANGED_SHARE
  return mostlyChanged || edits > REWRITE_CHANGED_RUNS
}

// A call's start time as a number, however the reporting API spells it — epoch
// milliseconds on some responses, an ISO string on others. Subtracting the raw
// values would yield NaN for the string form and leave the list in whatever
// order the endpoint happened to return.
export const toCallTimestamp = (startTime) => {
  if (isNil(startTime)) {
    return null
  }
  const parsed = typeof startTime === 'number' ? startTime : Date.parse(startTime)
  return Number.isNaN(parsed) ? null : parsed
}
